import { useEffect, useState } from "react";
import { Scale, Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DEMO_LAWS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Penal code / law book — fines, jail times, penalty points per charge.
   Everyone reads; command edits. These are the same charges the incident
   report form picks from and the courthouse sentences against. */

const CLS = {
  infraction: "text-zinc-300 border-white/25 bg-white/5",
  misdemeanor: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  felony: "text-red-300 border-red-500/40 bg-red-500/10",
};
export const jailLabel = (s) => s >= 3600 ? `${(s / 3600).toFixed(s % 3600 ? 1 : 0)}h` : s >= 60 ? `${Math.round(s / 60)}m` : s ? `${s}s` : "—";

export default function LawsTab({ G }) {
  const { serverId, token, demo, sfx, me } = G;
  const [laws, setLaws] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (demo) { setLaws(DEMO_LAWS); return; }
    api.get(`/nc/servers/${serverId}/cad/penal`, { params: { token } })
      .then((r) => setLaws(r.data.laws)).catch(() => setLaws([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { code: editing.code, title: editing.title, law_class: editing.class, fine: +editing.fine || 0, jail_seconds: +editing.jail_seconds || 0, points: +editing.points || 0, description: editing.description || "" };
    sfx.keyTick();
    if (demo) {
      setLaws((l) => editing.id ? l.map((x) => x.id === editing.id ? { ...x, ...body, class: body.law_class } : x) : [{ ...body, class: body.law_class, id: `l${Date.now()}` }, ...l]);
      setEditing(null); return;
    }
    setBusy(true);
    try {
      const r = editing.id
        ? await api.put(`/nc/servers/${serverId}/cad/penal/${editing.id}`, body, { params: { token } })
        : await api.post(`/nc/servers/${serverId}/cad/penal`, body, { params: { token } });
      toastOk(r.data.message); setEditing(null); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };

  if (!laws) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;
  const shown = filter ? laws.filter((l) => l.class === filter) : laws;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="laws-tab">
      <div className="flex items-center gap-1.5 flex-wrap">
        {["", "infraction", "misdemeanor", "felony"].map((c) => (
          <button key={c || "all"} data-testid={`law-filter-${c || "all"}`} onClick={() => { setFilter(c); sfx.keyTick(); }}
            className={`${btnCls} ${filter === c ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{c || "All"}</button>
        ))}
        {me.is_command && (
          <button data-testid="law-new" onClick={() => { setEditing({ code: "", title: "", class: "misdemeanor", fine: 0, jail_seconds: 0, points: 0, description: "" }); sfx.keyTick(); }}
            className={`${btnCls} border-sky-500/50 text-sky-300 hover:bg-sky-500/10 ml-auto flex items-center gap-1`}><Plus className="w-3 h-3" /> Add law</button>
        )}
      </div>
      {editing && (
        <div className="border border-sky-500/30 bg-sky-500/5 p-3 grid grid-cols-2 gap-2" data-testid="law-editor">
          <input data-testid="law-code" value={editing.code} onChange={(e) => setEditing((s) => ({ ...s, code: e.target.value }))} placeholder="Code (FEL-08)" className={inputCls} />
          <input data-testid="law-title" value={editing.title} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} placeholder="Title" className={inputCls} />
          <select data-testid="law-class" value={editing.class} onChange={(e) => setEditing((s) => ({ ...s, class: e.target.value }))} className={inputCls}>
            <option value="infraction">Infraction</option><option value="misdemeanor">Misdemeanor</option><option value="felony">Felony</option>
          </select>
          <div className="flex gap-1.5">
            <label className="font-mono text-[8px] uppercase text-zinc-500 flex items-center gap-1">$<input data-testid="law-fine" type="number" value={editing.fine} onChange={(e) => setEditing((s) => ({ ...s, fine: e.target.value }))} className={`${inputCls} w-20`} /></label>
            <label className="font-mono text-[8px] uppercase text-zinc-500 flex items-center gap-1">Jail s<input data-testid="law-jail" type="number" value={editing.jail_seconds} onChange={(e) => setEditing((s) => ({ ...s, jail_seconds: e.target.value }))} className={`${inputCls} w-20`} /></label>
            <label className="font-mono text-[8px] uppercase text-zinc-500 flex items-center gap-1">Pts<input data-testid="law-points" type="number" value={editing.points} onChange={(e) => setEditing((s) => ({ ...s, points: e.target.value }))} className={`${inputCls} w-14`} /></label>
          </div>
          <textarea data-testid="law-desc" value={editing.description} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} rows={2} placeholder="Description" className={`${inputCls} col-span-2 resize-none`} />
          <div className="col-span-2 flex gap-2">
            <button data-testid="law-save" onClick={save} disabled={busy || !editing.code.trim() || !editing.title.trim()} className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`}>{busy ? "Saving…" : "Save law"}</button>
            <button data-testid="law-cancel" onClick={() => setEditing(null)} className={`${btnCls} border-white/15 text-zinc-400`}>Cancel</button>
          </div>
        </div>
      )}
      {shown.map((l) => (
        <div key={l.id} data-testid={`law-${l.code}`} className="border border-white/10 bg-black/40 p-3 flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] font-bold text-sky-300">{l.code}</span>
            <span className="font-mono text-[10.5px] font-bold text-zinc-100 flex-1 min-w-0">{l.title}</span>
            <span className={`font-mono text-[7.5px] uppercase border px-1.5 py-0.5 ${CLS[l.class]}`}>{l.class}</span>
            {me.is_command && (
              <span className="flex gap-1">
                <button data-testid={`law-edit-${l.code}`} onClick={() => { setEditing({ ...l }); sfx.keyTick(); }} className="text-zinc-500 hover:text-sky-300"><Pencil className="w-3 h-3" /></button>
                <button data-testid={`law-del-${l.code}`} onClick={() => { sfx.keyTick(); if (demo) setLaws((x) => x.filter((y) => y.id !== l.id)); else api.delete(`/nc/servers/${serverId}/cad/penal/${l.id}`, { params: { token } }).then(load); }} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </span>
            )}
          </div>
          <p className="font-mono text-[8.5px] text-zinc-500 leading-snug">{l.description}</p>
          <p className="font-mono text-[9px] text-zinc-300">
            Fine <span className="text-emerald-300 font-bold">${l.fine.toLocaleString()}</span>
            <span className="text-zinc-600"> · </span>Jail <span className="text-orange-300 font-bold">{jailLabel(l.jail_seconds)}</span>
            <span className="text-zinc-600"> · </span>Points <span className="text-sky-300 font-bold">{l.points}</span>
          </p>
        </div>
      ))}
      {shown.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-3 text-center">No {filter} statutes</p>}
    </div>
  );
}
